import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, ElementRef, inject, OnInit, TemplateRef, ViewChild, ChangeDetectorRef, effect, untracked } from "@angular/core";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { componentApi, JumpstartComponentsWkIconsModule, JumpstartComponentsModule } from "@wk/components-v3-angular17";

import {
    AnalyticsService,
    CardViewService,
    DashboardsService,
    FilterSortModalHandlerService,
    FiltersService,
    HelpersService,
    IFirmRolesService,
    LocalStorageService,
    MANAGER_ROLE_ID,
    PARTNER_ROLE_ID,
    PREPARER_ROLE_ID,
    REVIEWER_ROLE_ID,
    ResponseErrorHandlerService,
    IFirmGroupsService,
    AuthService,
} from "../shared";
import { isiFirmTaxDashboard } from "../views-interfaces/dashboard-model.interface";
import { AlertsFilterGeneratorService } from "./alerts-filter-generator.service";
import { AssigneesFilterGeneratorService } from "./assignees-role-filter-generator.service";
import { CategoriesFilterGeneratorService } from "./categories-filter-generator.service";
import { DeadlineFilterGeneratorService } from "./deadline-filter-generator.service";
import { DeliveryMethodsFilterGeneratorService } from "./delivery-methods-filter-generator.service";
import { FavoriteFilters } from "./favorite-filter-model";
import { CheckModel, MultiSelectModel, Option, TextInputModel } from "./filter-types";
import { GroupnameFilterGeneratorService } from "./groupname-filter-generator.service";
import { IfirmGroupFilterGeneratorService } from "./ifirm-group-filter-generator.service";
import { TagsFilterGeneratorService } from "./tags-filter-generator.service";

import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { PendingChangesPopupComponent } from "../shared/pending-changes-popup/pending-changes-popup.component";
import { SearchComponent } from "../search-component/search.component";
import { KendoMultiselectWrapper } from "../shared/components/kendo-multiselect-wrapper/kendo-multiselect-wrapper.component";
import { FormsModule } from "@angular/forms";
import { WkTranslatePipe } from "../shared/utils/wk-translate-pipe/wk-translate.pipe";

@Component({
    selector: "wk-filter-menu",
    templateUrl: "./filter-menu.component.html",
    styleUrls: ["./filter-menu.component.scss", "./filter-modals.scss"],
    host: { class: "wk-fm" },
    imports: [
        JumpstartComponentsWkIconsModule,
        JumpstartComponentsModule,
        SearchComponent,
        KendoMultiselectWrapper,
        FormsModule,
        WkTranslatePipe,
        TranslatePipe,
    ],
})
export class FilterMenuComponent implements OnInit {
    private readonly alertsFilterGeneratorService = inject(AlertsFilterGeneratorService);
    protected readonly assigneesFilterGeneratorService = inject(AssigneesFilterGeneratorService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly authService = inject(AuthService);
    private readonly cardViewService = inject(CardViewService);
    private readonly categoriesFilterGeneratorService = inject(CategoriesFilterGeneratorService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly deadlineFilterGeneratorService = inject(DeadlineFilterGeneratorService);
    private readonly deliveryMethodsFilterGeneratorService = inject(DeliveryMethodsFilterGeneratorService);
    private readonly groupnameFilterGeneratorService = inject(GroupnameFilterGeneratorService);
    private readonly helpersService = inject(HelpersService);
    private readonly ifirmGroupFilterGeneratorService = inject(IfirmGroupFilterGeneratorService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly tagsFilterGeneratorService = inject(TagsFilterGeneratorService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly modalCoordinationService = inject(FilterSortModalHandlerService);
    public readonly filtersService = inject(FiltersService);
    private readonly ifirmGroupsService = inject(IFirmGroupsService);

    alertsFilterModel: MultiSelectModel;
    categoriesFilterModel: MultiSelectModel;
    tagsFilterModel: MultiSelectModel;
    deliveryMethodsFilterModel: MultiSelectModel;
    groupnameFilterModel: TextInputModel;
    group1FilterModel: MultiSelectModel;
    group2FilterModel: MultiSelectModel;
    deadlineFilterModel: MultiSelectModel;
    cardsReadyToBeWorkedOnModel: CheckModel;
    deadlineApril30thModel: CheckModel;
    deadlineJune15thModel: CheckModel;
    isOpen!: boolean;
    accordionStates: Record<string, boolean> = {};
    headingId = "";
    linesCount = 3;
    isExpanded = false;
    morePillsCount = 0;
    isiFirmTaxDashboard = false;
    isCardsReadyToWorkedOn = false;
    isCardsReadyToWorkedOnDisabled = false;

    public changeDetector = inject(ChangeDetectorRef);
    writeState = componentApi.writeState.bind(this);
    favoritesFilters: FavoriteFilters[] = [];
    currentFavoriteFilter: FavoriteFilters | null = {
        id: "",
        name: "",
        active: false,
        selections: {},
    };

    isFavoriteFilterEditMode = false;
    hasPendingFavoriteFilterChanges = false;
    modalActionType: "save" | "rename" = "save";
    favoriteFiltersLocalStorageKey = "";
    pendingFavoriteToApply: FavoriteFilters | null = null;
    isGroup1Enabled = false;
    isGroup2Enabled = false;
    @ViewChild("saveFilterDialogue") saveFilterDialogue!: TemplateRef<any>;
    @ViewChild("deleteFilterDialogue") deleteFilterDialogue!: TemplateRef<any>;

    @ViewChild("filterNameInput") filterNameInput!: ElementRef;

    bsModalRef?: BsModalRef;
    // Suppresses the assignees-effect handleFilterChange() call while clearAllFilters/applyFavoriteFilter run, to fire it once at the end.
    private isBulkFilterUpdate = false;

    constructor() {
        effect(() => {
            const models = this.assigneesFilterGeneratorService.models();
            if (!models || models.length === 0) return;

            untracked(() => {
                models.forEach((model) => {
                    this.setAssigneeFilter(model);
                });
                if (!this.isBulkFilterUpdate) {
                    this.filtersService.handleFilterChange();
                }
            });
        });

        this.filtersService.filtersChangedEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.isCardsReadyToWorkedOn = this.shouldShowCardsReadyToBeWorkedOn().show;
            this.isCardsReadyToWorkedOnDisabled = this.shouldShowCardsReadyToBeWorkedOn().disabled;
            this.onAnyFilterChanged();
        });
        this.translateService.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {});
        this.modalCoordinationService.activeModal$.pipe(takeUntilDestroyed()).subscribe((activeModal) => {
            if (activeModal !== "filter" && activeModal !== "pendingChanges") {
                this.isOpen = false;
            }
            if (activeModal === "pendingChanges" && this.hasPendingFavoriteFilterChanges) {
                this.showPendingChangesPopup(() => {
                    this.isOpen = false;
                    this.modalCoordinationService.closeAllModals();
                    this.modalCoordinationService.setActiveModal("sort");
                });
            }
        });
        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed()).subscribe(async (currentDashboardId) => {
            this.isOpen = false;
            this.isFavoriteFilterEditMode = false;
            this.hasPendingFavoriteFilterChanges = false;
            this.clearCurrentFavoriteFilter();
            const currentDashboard = await this.dashboardsService.getCurrentDashboard();
            this.isiFirmTaxDashboard = isiFirmTaxDashboard(currentDashboard);
            const item = localStorage.getItem(`Filter_Selection_${currentDashboardId}`);
            const storedSelection = item && item !== "" ? JSON.parse(item) : {};
            this.favoriteFiltersLocalStorageKey = this.getFavoritesFiltersKey(currentDashboardId, this.authService.currentUser()?.sub);
            await this.loadFilters(currentDashboard, storedSelection);
            this.loadFavoriteFilters();
            this.applyActiveFavoriteOnLoad();
            await this.loadGroupEnabledState();
        });
        this.cardViewService.viewTypeChangeEvent$.pipe(takeUntilDestroyed()).subscribe(async (viewType) => {
            if (this.alertsFilterModel) {
                this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel(this.alertsFilterModel.selection, viewType);
                this.onAlertsChanged(this.alertsFilterModel.selection, false);
            }
        });
        this.helpersService.favouriteFiltersEditModeEvent$.pipe(takeUntilDestroyed()).subscribe((event) => {
            this.handleFilterEditMode(event);
        });
        this.helpersService.favouriteFiltersPendingChangesEvent$.pipe(takeUntilDestroyed()).subscribe((event) => {
            this.hasPendingFavoriteFilterChanges = event;
        });
        this.helpersService.revertToFavoriteFilterEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            if (this.currentFavoriteFilter?.id) {
                // Disable edit mode before applying to prevent triggering pending changes
                this.isFavoriteFilterEditMode = false;
                this.applyFavoriteFilter(this.currentFavoriteFilter);
            }
        });
    }

    ngOnInit(): void {
        this.isOpen = false;
        this.accordionStates["Accordion_Filters_General"] = this.getAccordionState("Accordion_Filters_General");
        this.accordionStates["Accordion_Filters_Active"] = this.getAccordionState("Accordion_Filters_Active");
        this.accordionStates["Accordion_Filters_Assignees"] = this.getAccordionState("Accordion_Filters_Assignees");
        this.accordionStates["Accordion_Filters_Favorites"] = this.getAccordionState("Accordion_Filters_Favorites");
        this.initializeFilters();
    }

    private async initializeFilters(): Promise<void> {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isiFirmTaxDashboard = isiFirmTaxDashboard(currentDashboard);
        await this.loadGroupEnabledState();
        const item = localStorage.getItem(`Filter_Selection_${currentDashboard.Id}`);
        const storedSelection = item && item !== "" ? JSON.parse(item) : {};
        this.favoriteFiltersLocalStorageKey = this.getFavoritesFiltersKey(currentDashboard.Id, this.authService.currentUser()?.sub);
        await this.loadFilters(currentDashboard, storedSelection);
        this.loadFavoriteFilters();
        this.applyActiveFavoriteOnLoad();
    }

    async loadFilters(currentDashboard, storedSelection) {
        const viewType = this.cardViewService.currentViewType;

        this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel(storedSelection.Alerts ?? [], viewType);
        this.categoriesFilterModel = await this.categoriesFilterGeneratorService.generateModel(storedSelection.Categories ?? []);
        this.tagsFilterModel = this.tagsFilterGeneratorService.generateModel(storedSelection.Tags ?? [], this.isiFirmTaxDashboard);
        this.deliveryMethodsFilterModel = await this.deliveryMethodsFilterGeneratorService.generateModel(
            storedSelection?.DeliveryMethods ?? [],
            currentDashboard,
        );
        this.groupnameFilterModel = this.groupnameFilterGeneratorService.generateModel(storedSelection.Groupname ?? "");
        this.group1FilterModel = await this.ifirmGroupFilterGeneratorService.generateGroup1Model(storedSelection.Group1 ?? []);
        this.group2FilterModel = await this.ifirmGroupFilterGeneratorService.generateGroup2Model(storedSelection.Group2 ?? []);
        this.assigneesFilterGeneratorService.loadModels(storedSelection.Assignees);
        this.cardsReadyToBeWorkedOnModel = {
            label: "FiltersSidebar.Options.AssignedToRadioButtons.ReadyToBeWorked",
            selected: storedSelection.ReadyToBeWorkedOn,
        };
        this.deadlineApril30thModel = this.deadlineFilterGeneratorService.generateApril30thModel(storedSelection.Deadline?.hasApril30th ?? false);
        this.deadlineJune15thModel = this.deadlineFilterGeneratorService.generateJune15thModel(storedSelection.Deadline?.hasJune15th ?? false);

        this.setAlertsFilter(this.alertsFilterModel.selection);
        this.setCategoriesFilter(this.categoriesFilterModel.selection);
        this.setTagsFilter(this.tagsFilterModel.selection);
        this.setDeliveryMethodsFilter(this.deliveryMethodsFilterModel.selection);
        this.setGroupnameFilter(this.groupnameFilterModel.text);
        await this.setGroup1Filter(this.group1FilterModel.selection);
        await this.setGroup2Filter(this.group2FilterModel.selection);
        this.setDeadlineFilter(this.deadlineApril30thModel.selected, this.deadlineJune15thModel.selected);
        this.filtersService.handleFilterChange();
    }

    async onAlertsChanged(selectedOptions: Option[], trackAnalytics = false) {
        this.handleFavoriteFilterOnSelectionChange("alerts", selectedOptions, trackAnalytics);
        await this.storeAndSetAlertsFilter(selectedOptions);
        this.filtersService.handleFilterChange(trackAnalytics);
    }

    async storeAndSetAlertsFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Alerts"], selectedOptions);
        this.setAlertsFilter(selectedOptions);
    }

    setAlertsFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.alertsFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Alerts");
            this.filtersService.activeFiltersForPrint.delete("Alerts");
        } else {
            this.filtersService.activeFilters.set("Alerts", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Alerts",
                this.alertsFilterGeneratorService.getPrintDescription(this.alertsFilterModel.label, selectedOptions),
            );
        }
    }

    async onCategoriesChanged(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("categories", selectedOptions);
        await this.storeAndSetCategoriesFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetCategoriesFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Categories"], selectedOptions);
        this.setCategoriesFilter(selectedOptions);
    }

    setCategoriesFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.categoriesFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Categories");
            this.filtersService.activeFiltersForPrint.delete("Categories");
        } else {
            this.filtersService.activeFilters.set("Categories", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Categories",
                this.categoriesFilterGeneratorService.getPrintDescription(this.categoriesFilterModel.label, selectedOptions),
            );
        }
    }

    async onTagsChanged(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("tags", selectedOptions);
        await this.storeAndSetTagsFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetTagsFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Tags"], selectedOptions);
        this.setTagsFilter(selectedOptions);
    }

    setTagsFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.tagsFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Tags");
            this.filtersService.activeFiltersForPrint.delete("Tags");
        } else {
            this.filtersService.activeFilters.set("Tags", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Tags",
                this.tagsFilterGeneratorService.getPrintDescription(this.tagsFilterModel.label, selectedOptions),
            );
        }
    }

    async onGroupnameChanged(input: string) {
        this.handleFavoriteFilterOnSelectionChange("groupname", input);
        await this.storeAndSetGroupnameFilter(input);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetGroupnameFilter(input: string) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Groupname"], input);
        this.setGroupnameFilter(input);
    }

    setGroupnameFilter(input: string) {
        const filter = this.groupnameFilterGeneratorService.generateFilter(input);
        if (!filter) {
            this.filtersService.activeFilters.delete("Groupname");
            this.filtersService.activeFiltersForPrint.delete("Groupname");
        } else {
            this.filtersService.activeFilters.set("Groupname", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Groupname",
                this.groupnameFilterGeneratorService.getPrintDescription(this.groupnameFilterModel.label, input),
            );
        }
    }

    async onGroup1Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("group1", selectedOptions);
        await this.storeAndSetGroup1Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetGroup1Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Group1"], selectedOptions);
        await this.setGroup1Filter(selectedOptions);
    }

    async setGroup1Filter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.ifirmGroupFilterGeneratorService.generateGroup1Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Group1");
            this.filtersService.activeFiltersForPrint.delete("Group1");
        } else {
            this.filtersService.activeFilters.set("Group1", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Group1",
                this.ifirmGroupFilterGeneratorService.getPrintDescription(this.group1FilterModel.label, selectedOptions),
            );
        }
    }

    async onGroup2Changed(selectedOptions: Option[]) {
        this.handleFavoriteFilterOnSelectionChange("group2", selectedOptions);
        await this.storeAndSetGroup2Filter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetGroup2Filter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Group2"], selectedOptions);
        await this.setGroup2Filter(selectedOptions);
    }

    async setGroup2Filter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = await this.ifirmGroupFilterGeneratorService.generateGroup2Filter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("Group2");
            this.filtersService.activeFiltersForPrint.delete("Group2");
        } else {
            this.filtersService.activeFilters.set("Group2", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Group2",
                this.ifirmGroupFilterGeneratorService.getPrintDescription(this.group2FilterModel.label, selectedOptions),
            );
        }
    }

    async onDeadlineChanged() {
        this.handleFavoriteFilterOnSelectionChange("deadline", {
            april30th: this.deadlineApril30thModel.selected,
            june15th: this.deadlineJune15thModel.selected,
        });
        await this.storeAndSetDeadlineFilter(this.deadlineApril30thModel.selected, this.deadlineJune15thModel.selected);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetDeadlineFilter(hasApril30th?: boolean, hasJune15th?: boolean) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Deadline", "hasApril30th"], hasApril30th);
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Deadline", "hasJune15th"], hasJune15th);
        this.setDeadlineFilter(hasApril30th, hasJune15th);
    }

    async setDeadlineFilter(hasApril30th, hasJune15th) {
        const filter = await this.deadlineFilterGeneratorService.generateFilter(hasApril30th, hasJune15th);
        if (!filter) {
            this.filtersService.activeFilters.delete("Deadline");
            this.filtersService.activeFiltersForPrint.delete("Deadline");
        } else {
            this.filtersService.activeFilters.set("Deadline", filter);
            this.filtersService.activeFiltersForPrint.set(
                "Deadline",
                this.deadlineFilterGeneratorService.getPrintDescription(this.deadlineFilterModel?.label, hasApril30th, hasJune15th),
            );
        }
    }

    async onAssigneeChanged(model: MultiSelectModel) {
        const newModel = this.assigneesFilterGeneratorService.updateModel(model.id, model.selection);
        this.handleFavoriteFilterOnSelectionChange("assignees", newModel.selection);
        await this.storeAndSetAssigneeFilter(newModel);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetAssigneeFilter(model: MultiSelectModel) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["Assignees", model.id], model.selection);
        this.setAssigneeFilter(model);
    }

    setAssigneeFilter(model: MultiSelectModel) {
        const selectedValues = model.selection?.map((option) => option && (option.value as string)) ?? [];
        const filter = this.assigneesFilterGeneratorService.generateFilter(selectedValues, this.cardsReadyToBeWorkedOnModel?.selected ?? false);
        if (!filter) {
            this.filtersService.activeFilters.delete(model.id);
            this.filtersService.activeFiltersForPrint.delete(model.id);
        } else {
            this.filtersService.activeFilters.set(model.id, filter);
            this.filtersService.activeFiltersForPrint.set(
                model.id,
                this.assigneesFilterGeneratorService.getPrintDescription(model.label, selectedValues),
            );
        }
    }

    async onReadyToBeWorkedOnChanged() {
        await this.storeReadyToBeWorkedOn(this.cardsReadyToBeWorkedOnModel.selected);
        this.assigneesFilterGeneratorService.models().forEach((model) => {
            this.setAssigneeFilter(model);
        });
        this.filtersService.handleFilterChange(true);
    }

    async storeReadyToBeWorkedOn(readyToBeWorkedOn) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["ReadyToBeWorkedOn"], readyToBeWorkedOn);
    }

    async clearAllFilters() {
        this.isBulkFilterUpdate = true;
        try {
            await this.clearAllFiltersCore();
        } finally {
            this.isBulkFilterUpdate = false;
        }
        this.filtersService.handleFilterChange(true);
    }

    private async clearAllFiltersCore() {
        const viewType = this.cardViewService.currentViewType;
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.alertsFilterModel = this.alertsFilterGeneratorService.generateModel([], viewType);
        this.tagsFilterModel = this.tagsFilterGeneratorService.generateModel([], this.isiFirmTaxDashboard);
        this.groupnameFilterModel = this.groupnameFilterGeneratorService.generateModel("");
        // Independent model generations run concurrently instead of a sequential await chain.
        [this.categoriesFilterModel, this.deliveryMethodsFilterModel, this.group1FilterModel, this.group2FilterModel] = await Promise.all([
            this.categoriesFilterGeneratorService.generateModel([]),
            this.deliveryMethodsFilterGeneratorService.generateModel([], currentDashboard),
            this.ifirmGroupFilterGeneratorService.generateGroup1Model([]),
            this.ifirmGroupFilterGeneratorService.generateGroup2Model([]),
        ]);
        this.deadlineJune15thModel.selected = false;
        this.deadlineApril30thModel.selected = false;
        this.assigneesFilterGeneratorService.resetCache();
        this.cardsReadyToBeWorkedOnModel.selected = false;

        // Independent filter writes run concurrently instead of a sequential await chain.
        await Promise.all([
            this.storeAndSetAlertsFilter(this.alertsFilterModel.selection),
            this.storeAndSetCategoriesFilter(this.categoriesFilterModel.selection),
            this.storeAndSetTagsFilter(this.tagsFilterModel.selection),
            this.storeAndSetDeliveryMethodsFilter(this.deliveryMethodsFilterModel.selection),
            this.storeAndSetGroupnameFilter(this.groupnameFilterModel.text),
            this.storeAndSetGroup1Filter(this.group1FilterModel.selection),
            this.storeAndSetGroup2Filter(this.group2FilterModel.selection),
            this.storeAndSetDeadlineFilter(this.deadlineApril30thModel.selected, this.deadlineJune15thModel.selected),
            this.storeReadyToBeWorkedOn(this.cardsReadyToBeWorkedOnModel.selected),
        ]);
        this.assigneesFilterGeneratorService.resetCache();
        this.assigneesFilterGeneratorService.models().forEach((model) => {
            model.selection = [];
            this.storeAndSetAssigneeFilter(model);
        });

        this.isFavoriteFilterEditMode = false;
        this.favoritesFilters.forEach((f) => (f.active = false));
        localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(this.favoritesFilters));
        this.clearCurrentFavoriteFilter();
    }

    open() {
        if (this.hasPendingFavoriteFilterChanges) {
            this.showPendingChangesPopup(() => {
                this.isOpen = false;
                this.modalCoordinationService.closeAllModals();
            });
            return;
        }
        this.isOpen = !this.isOpen;
        this.isFavoriteFilterEditMode = false;
        this.writeState("isExpanded", false);
        if (this.isOpen) {
            // Set active modal to filter first, then close others to avoid race condition
            this.modalCoordinationService.setActiveModal("filter");
        } else {
            // Close all modals when closing filter
            this.modalCoordinationService.closeAllModals();
        }
    }

    close(reason?: "overlay" | "esc" | "closeButton") {
        if (reason === "overlay") {
            return;
        }
        if (reason === "esc") {
            if (this.hasPendingFavoriteFilterChanges) {
                this.showPendingChangesPopup(() => {
                    this.isOpen = false;
                    this.modalCoordinationService.closeAllModals();
                });
                return;
            }
        }
        this.isOpen = false;
        this.modalCoordinationService.closeAllModals();
    }

    saveAccordionState(key: string, isOpen: boolean) {
        LocalStorageService.setObject(`Accordion_States_${this.authService.currentUser()?.sub}`, [key], isOpen);
    }

    getAccordionState(key: string, defaultValue = true): boolean {
        const item = localStorage.getItem(`Accordion_States_${this.authService.currentUser()?.sub}`);

        let value: any = defaultValue;
        if (item) {
            const parsed = JSON.parse(item);
            if (parsed && typeof parsed === "object" && key in parsed) {
                value = parsed[key];
            }
        }
        return value;
    }

    onAccordionOpen(event: boolean, key: string): void {
        if (event) {
            this.accordionStates[key] = true;
            this.saveAccordionState(key, true);
        } else {
            this.accordionStates[key] = false;
            this.saveAccordionState(key, false);
        }
    }

    onAccordionClose(key: string): void {
        this.accordionStates[key] = false;
        this.saveAccordionState(key, false);
    }

    onPillClose(model: any, sel: any, changeHandler: (selection: any) => void) {
        if (!model || !Array.isArray(model.selection)) return;
        model.selection = model.selection.filter((s: any) => s.value !== sel.value);
        changeHandler.name == "onAssigneeChanged" ? changeHandler.call(this, model) : changeHandler.call(this, model.selection);
    }

    shouldShowCardsReadyToBeWorkedOn(): { show: boolean; disabled: boolean } {
        if (!this.assigneesFilterGeneratorService.models() || !this.assigneesFilterGeneratorService.models().length)
            return { show: false, disabled: true };
        const selections = this.assigneesFilterGeneratorService.models().filter((mod) => mod?.selection.length > 0);

        const hasNonUnassigned = selections.some((sel) => sel.selection.some((option) => option.text !== "FiltersSidebar.Options.Users.Unassigned"));

        const hasUnassigned = selections.some((sel) => sel.selection.some((option) => option.text === "FiltersSidebar.Options.Users.Unassigned"));

        const hasOnlyAssignedUsers = hasNonUnassigned && !hasUnassigned;
        const isMultipleWithUnassigned = selections.length > 1 && hasUnassigned;

        return {
            show: hasOnlyAssignedUsers,
            disabled: selections.length === 1 ? !hasOnlyAssignedUsers : !isMultipleWithUnassigned,
        };
    }

    onExpandButtonToggle(): void {
        this.writeState("isExpanded", !this.isExpanded);
    }
    onVisiblePillsChange(event: { morePillsCount: number }): void {
        this.writeState("morePillsCount", event.morePillsCount);
    }

    getFavoritesFiltersKey(currentDashboardId: string, userId?: string): string {
        const type = this.isiFirmTaxDashboard ? "iFirm" : "Taxprep";
        return `FavoritesFilters_${type}_${currentDashboardId}_${userId}`;
    }

    loadFavoriteFilters() {
        this.saveDefaultFavoriteFilter();
        this.favoritesFilters = this.getFavoritesFilters();
        const activeFavoritesFilters = this.favoritesFilters.find((f) => f.active);
        this.currentFavoriteFilter = activeFavoritesFilters ?? {
            id: "",
            name: "",
            active: false,
            selections: {},
        };
    }

    applyActiveFavoriteOnLoad() {
        const activeFavorite = this.favoritesFilters.find((f) => f.active);
        if (activeFavorite) {
            this.applyFavoriteFilter(activeFavorite);
        }
    }

    async applyFavoriteFilter(favorite: FavoriteFilters) {
        this.isBulkFilterUpdate = true;
        try {
            await this.applyFavoriteFilterCore(favorite);
        } finally {
            this.isBulkFilterUpdate = false;
        }
        this.filtersService.handleFilterChange();
        this.currentFavoriteFilter = favorite;
    }

    private async applyFavoriteFilterCore(favorite: FavoriteFilters) {
        // 1. Clear all selections and active filters
        this.alertsFilterModel.selection = [];
        this.setAlertsFilter([]);
        this.categoriesFilterModel.selection = [];
        this.tagsFilterModel.selection = [];
        this.setTagsFilter([]);
        this.deliveryMethodsFilterModel.selection = [];
        this.setDeliveryMethodsFilter([]);
        this.groupnameFilterModel.text = "";
        this.setGroupnameFilter("");
        this.group1FilterModel.selection = [];
        this.group2FilterModel.selection = [];
        this.deadlineApril30thModel.selected = false;
        this.deadlineJune15thModel.selected = false;
        this.cardsReadyToBeWorkedOnModel.selected = false;
        if (Array.isArray(this.assigneesFilterGeneratorService.models())) {
            this.assigneesFilterGeneratorService.models().forEach((model, idx) => {
                this.assigneesFilterGeneratorService.updateSelection(idx, []);
                model.selection = [];
                this.setAssigneeFilter(model);
            });
        }
        await this.dashboardsService.getCurrentDashboard();
        this.setCategoriesFilter([]);
        // Independent filter recomputations run concurrently instead of a sequential await chain.
        await Promise.all([
            this.setGroup1Filter([]),
            this.setGroup2Filter([]),
            this.setDeadlineFilter(false, false),
            this.storeReadyToBeWorkedOn(false),
        ]);

        // 2. Apply favorite selections and update active filters
        if (favorite.selections.alerts) {
            this.alertsFilterModel.selection = favorite.selections.alerts;
            this.setAlertsFilter(this.alertsFilterModel.selection);
        }
        await this.dashboardsService.getCurrentDashboard();
        if (favorite.selections.categories) {
            this.categoriesFilterModel.selection = favorite.selections.categories;
            this.setCategoriesFilter(this.categoriesFilterModel.selection);
        }
        if (favorite.selections.tags) {
            this.tagsFilterModel.selection = favorite.selections.tags;
            this.setTagsFilter(this.tagsFilterModel.selection);
        }
        if (favorite.selections.deliveryMethods) {
            this.deliveryMethodsFilterModel.selection = favorite.selections.deliveryMethods;
            this.setDeliveryMethodsFilter(this.deliveryMethodsFilterModel.selection);
        }
        if (favorite.selections.groupname !== undefined) {
            this.groupnameFilterModel.text = favorite.selections.groupname;
            this.setGroupnameFilter(this.groupnameFilterModel.text);
        }
        // Independent filter recomputations are collected and run concurrently below instead of awaited one by one.
        const applyFilterPromises: Promise<unknown>[] = [];
        if (favorite.selections.group1) {
            this.group1FilterModel.selection = favorite.selections.group1;
            applyFilterPromises.push(this.setGroup1Filter(this.group1FilterModel.selection));
        }
        if (favorite.selections.group2) {
            this.group2FilterModel.selection = favorite.selections.group2;
            applyFilterPromises.push(this.setGroup2Filter(this.group2FilterModel.selection));
        }
        if (favorite.selections.deadlineApril30th !== undefined) {
            this.deadlineApril30thModel.selected = favorite.selections.deadlineApril30th;
        }
        if (favorite.selections.deadlineJune15th !== undefined) {
            this.deadlineJune15thModel.selected = favorite.selections.deadlineJune15th;
        }
        applyFilterPromises.push(this.setDeadlineFilter(this.deadlineApril30thModel.selected, this.deadlineJune15thModel.selected));

        if (favorite.selections.readyToBeWorkedOn !== undefined) {
            this.cardsReadyToBeWorkedOnModel.selected = favorite.selections.readyToBeWorkedOn;
            applyFilterPromises.push(this.storeReadyToBeWorkedOn(this.cardsReadyToBeWorkedOnModel.selected));
        }
        await Promise.all(applyFilterPromises);

        if (favorite.selections.assignees && Array.isArray(favorite.selections.assignees)) {
            favorite.selections.assignees.forEach((sel, idx) => {
                if (this.assigneesFilterGeneratorService.models()[idx]) {
                    const model = this.assigneesFilterGeneratorService.updateSelection(idx, sel);
                    this.setAssigneeFilter(model);
                }
            });
        }

        this.applyAssignedToMeFavorite(favorite);
    }

    // Special handling for "Assigned to me"
    private applyAssignedToMeFavorite(favorite: FavoriteFilters) {
        if (favorite.id === "assigned-to-me" && this.assigneesFilterGeneratorService.models().length) {
            const currentUserId = this.authService.currentUser()?.sub;
            const userOption = this.assigneesFilterGeneratorService.models()[0]?.allOptions?.find((opt) => {
                try {
                    const valueObj = typeof opt.value === "string" ? JSON.parse(opt.value) : opt.value;
                    return valueObj && valueObj.userId === currentUserId;
                } catch {
                    return false;
                }
            });
            if (userOption) {
                const model = this.assigneesFilterGeneratorService.updateSelection(0, [userOption]);
                this.setAssigneeFilter(model);

                favorite.selections.assignees = [[userOption]];
                // Save to localStorage
                const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
                const idx = existingFilters.findIndex((f) => f.id === "assigned-to-me");
                if (idx !== -1) {
                    existingFilters[idx].selections.assignees = [[userOption]];
                    localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
                }
            }
        }
    }

    async toggleFavorite(model) {
        if (this.isFavoriteFilterEditMode && !this.hasPendingFavoriteFilterChanges) {
            this.isFavoriteFilterEditMode = false;
        }
        if (this.isFavoriteFilterEditMode && this.hasPendingFavoriteFilterChanges) {
            this.pendingFavoriteToApply = model;
            this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
                initialState: {
                    onDiscard: () => {
                        this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                        this.isFavoriteFilterEditMode = false;
                        // Apply the new favorite filter directly instead of reverting to current
                        const newFavorite = this.pendingFavoriteToApply;
                        this.pendingFavoriteToApply = null;
                        this.currentFavoriteFilter = newFavorite;
                        this.toggleFavorite(newFavorite);
                    },
                    onKeepEditing: () => {
                        // Clear pending favorite
                        this.pendingFavoriteToApply = null;
                    },
                },
                class: "custom-modal-rename-dashboard",
            });
            // Pass the modal ref to the component after it's created
            if (this.bsModalRef.content) {
                this.bsModalRef.content.bsModalRef = this.bsModalRef;
            }
            return;
        }

        if (this.isFavoriteFilterEditMode && !this.hasPendingFavoriteFilterChanges) {
            this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
        }

        const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
        const index = existingFilters.findIndex((f) => f.id === model.id);

        if (index !== -1) {
            existingFilters.forEach((f) => (f.active = false));

            existingFilters[index].active = !model.active;
            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
            this.favoritesFilters = this.getFavoritesFilters();

            if (existingFilters[index].active) {
                await this.applyFavoriteFilter(existingFilters[index]);
            } else {
                this.clearAllFilters();
                this.currentFavoriteFilter = {
                    id: "",
                    name: "",
                    active: false,
                    selections: {},
                };
            }
        }
    }

    saveFavoritesFilter(filterName: string) {
        try {
            const existingFilters = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
            const currentFilterId = this.modalActionType === "rename" || this.isFavoriteFilterEditMode ? this.currentFavoriteFilter?.id : null;
            const hasDuplicateName = existingFilters.some(
                (filter: FavoriteFilters) =>
                    filter.name.toLowerCase() === filterName.toLowerCase() && (!currentFilterId || filter.id !== currentFilterId),
            );
            if (hasDuplicateName) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "FILTER-SAVE-ERROR",
                    messageCode: "duplicateFavoriteFilterName",
                    additionInfo: this.translateService.instant("NotifyMessages.DuplicateFavoriteFilterNameContent"),
                });
                return;
            }

            if (this.modalActionType === "rename" && this.currentFavoriteFilter?.id) {
                const idx = existingFilters.findIndex((f) => f.id === this.currentFavoriteFilter?.id);
                if (idx !== -1) {
                    existingFilters[idx].name = filterName;
                }
            } else if (this.isFavoriteFilterEditMode && this.currentFavoriteFilter?.id) {
                const idx = existingFilters.findIndex((f) => f.id === this.currentFavoriteFilter?.id);
                if (idx !== -1) {
                    existingFilters[idx].name = filterName;
                    existingFilters[idx].selections = {
                        alerts: this.alertsFilterModel?.selection ?? [],
                        categories: this.categoriesFilterModel?.selection ?? [],
                        tags: this.tagsFilterModel?.selection ?? [],
                        deliveryMethods: this.deliveryMethodsFilterModel?.selection ?? [],
                        groupname: this.groupnameFilterModel?.text ?? "",
                        group1: this.group1FilterModel?.selection ?? [],
                        group2: this.group2FilterModel?.selection ?? [],
                        deadlineApril30th: this.deadlineApril30thModel?.selected ?? false,
                        deadlineJune15th: this.deadlineJune15thModel?.selected ?? false,
                        assignees: this.assigneesFilterGeneratorService.models().map((m) => m.selection) ?? [],
                        readyToBeWorkedOn: this.cardsReadyToBeWorkedOnModel?.selected ?? false,
                    };
                    this.currentFavoriteFilter = existingFilters[idx];
                    this.trackSaveFilterAnalytics("Update", existingFilters[idx].selections);
                }
                this.isFavoriteFilterEditMode = false;
                this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
            } else {
                const filter: FavoriteFilters = {
                    id: Date.now().toString(),
                    name: filterName,
                    active: true,
                    selections: {
                        alerts: this.alertsFilterModel?.selection ?? [],
                        categories: this.categoriesFilterModel?.selection ?? [],
                        tags: this.tagsFilterModel?.selection ?? [],
                        deliveryMethods: this.deliveryMethodsFilterModel?.selection ?? [],
                        groupname: this.groupnameFilterModel?.text ?? "",
                        group1: this.group1FilterModel?.selection ?? [],
                        group2: this.group2FilterModel?.selection ?? [],
                        deadlineApril30th: this.deadlineApril30thModel?.selected ?? false,
                        deadlineJune15th: this.deadlineJune15thModel?.selected ?? false,
                        assignees: this.assigneesFilterGeneratorService.models().map((m) => m.selection) ?? [],
                        readyToBeWorkedOn: this.cardsReadyToBeWorkedOnModel?.selected ?? false,
                    },
                };
                existingFilters.push(filter);
                this.currentFavoriteFilter = filter;
                this.trackSaveFilterAnalytics("Create", filter.selections);
                this.isFavoriteFilterEditMode = false;
                this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
            }

            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(existingFilters));
            this.favoritesFilters = this.getFavoritesFilters();
            this.bsModalRef?.hide();
        } catch {
            this.bsModalRef?.hide();
            this.responseErrorHandlerService.showPopup({
                typeLayout: "FILTER-SAVE-ERROR",
                messageCode: "filterSaveError",
                additionInfo: this.translateService.instant("NotifyMessages.FavoritesFiltersSaveFailContent"),
            });
        }
    }

    saveDefaultFavoriteFilter() {
        const favorites: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
        const exists = favorites.some((f) => f.id === "assigned-to-me");
        if (!exists) {
            favorites.unshift({
                id: "assigned-to-me",
                name: "Assigned to me",
                active: false,
                selections: {},
            });

            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(favorites));
        }
    }

    enableFavoriteFilterEditMode(isEnabled: boolean) {
        if (this.hasPendingFavoriteFilterChanges) {
            this.showPendingChangesPopup(() => {
                this.helpersService.favouriteFiltersEditModeEmitter.next(isEnabled);
            });
        } else {
            this.helpersService.favouriteFiltersEditModeEmitter.next(isEnabled);
        }
    }

    handleFilterEditMode(isEnabled: boolean) {
        this.isFavoriteFilterEditMode = isEnabled;
    }

    deleteFavoriteFilter(filterId: string): void {
        try {
            const existingFilters: FavoriteFilters[] = JSON.parse(localStorage.getItem(this.favoriteFiltersLocalStorageKey) || "[]");
            const updatedFilters = existingFilters.filter((f) => f.id !== filterId);
            localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(updatedFilters));
            this.favoritesFilters = this.getFavoritesFilters();
            this.bsModalRef?.hide();
            this.clearCurrentFavoriteFilter();
            this.clearAllFilters();
        } catch {
            this.bsModalRef?.hide();
        }
    }

    clearCurrentFavoriteFilter() {
        this.currentFavoriteFilter = {
            id: "",
            name: "",
            active: false,
            selections: {},
        };
    }

    async handleFavoriteFilterOnSelectionChange(
        filterType: string,
        selectedOptions: Option[] | string | boolean | { april30th: boolean; june15th: boolean },
        resetFavorite = true,
    ) {
        if (!this.isFavoriteFilterEditMode && this.currentFavoriteFilter && this.currentFavoriteFilter.id.length > 0) {
            const fav = this.currentFavoriteFilter.selections;

            // 1. Set all filter models from favorite filter selections
            this.applyFavoriteSelectionsToModels(fav);

            // 2. Overwrite the changed filter model with the user's new selection
            switch (filterType) {
                case "alerts":
                    this.alertsFilterModel.selection = selectedOptions as Option[];
                    break;
                case "categories":
                    this.categoriesFilterModel.selection = selectedOptions as Option[];
                    break;
                case "tags":
                    this.tagsFilterModel.selection = selectedOptions as Option[];
                    break;
                case "deliveryMethods":
                    this.deliveryMethodsFilterModel.selection = selectedOptions as Option[];
                    break;
                case "groupname":
                    this.groupnameFilterModel.text = selectedOptions as string;
                    break;
                case "group1":
                    this.group1FilterModel.selection = selectedOptions as Option[];
                    break;
                case "group2":
                    this.group2FilterModel.selection = selectedOptions as Option[];
                    break;
                case "deadline":
                    if (
                        typeof selectedOptions === "object" &&
                        selectedOptions !== null &&
                        "april30th" in selectedOptions &&
                        "june15th" in selectedOptions
                    ) {
                        this.deadlineApril30thModel.selected = (selectedOptions as { april30th: boolean; june15th: boolean }).april30th;
                        this.deadlineJune15thModel.selected = (selectedOptions as { april30th: boolean; june15th: boolean }).june15th;
                    }
                    break;
            }

            // 3. Store all filter selections in local cache
            await this.storeAllFilterSelections();

            // 4. Deactivate favorite filter and clear currentFavoriteFilter
            if (resetFavorite) {
                this.favoritesFilters.forEach((f) => (f.active = false));
                localStorage.setItem(this.favoriteFiltersLocalStorageKey, JSON.stringify(this.favoritesFilters));
                this.clearCurrentFavoriteFilter();
            }

            // 5. Notify filter change
            this.filtersService.handleFilterChange();
        }
    }

    private applyFavoriteSelectionsToModels(fav: FavoriteFilters["selections"]) {
        this.alertsFilterModel.selection = fav.alerts ?? [];
        this.categoriesFilterModel.selection = fav.categories ?? [];
        this.tagsFilterModel.selection = fav.tags ?? [];
        this.deliveryMethodsFilterModel.selection = fav.deliveryMethods ?? [];
        this.groupnameFilterModel.text = fav.groupname ?? "";
        this.group1FilterModel.selection = fav.group1 ?? [];
        this.group2FilterModel.selection = fav.group2 ?? [];
        this.deadlineApril30thModel.selected = fav.deadlineApril30th ?? false;
        this.deadlineJune15thModel.selected = fav.deadlineJune15th ?? false;

        if (Array.isArray(this.assigneesFilterGeneratorService.models()) && Array.isArray(fav.assignees)) {
            this.assigneesFilterGeneratorService.models().forEach((model, idx) => {
                const favAssignees = fav.assignees?.[idx] ?? [];
                const modelSelection = model.selection ?? [];
                if (modelSelection.length > 0 && favAssignees.length === 0) {
                    // Keep model.selection as is
                    model.selection = modelSelection;
                } else if (modelSelection.length > 0 && favAssignees.length > 0) {
                    // Merge both, avoiding duplicates by value
                    const merged = [
                        ...modelSelection,
                        ...favAssignees.filter((favOpt) => !modelSelection.some((selOpt) => selOpt.value === favOpt.value)),
                    ];
                    model.selection = merged;
                } else if (modelSelection.length === 0 && favAssignees.length > 0 && this.currentFavoriteFilter?.id != "assigned-to-me") {
                    // Set fav.assignees to model.selection
                    model.selection = favAssignees;
                } else {
                    // Both empty, keep as empty
                    model.selection = [];
                }
            });
        }
    }

    private async storeAllFilterSelections() {
        await this.storeAndSetAlertsFilter(this.alertsFilterModel.selection);
        await this.storeAndSetCategoriesFilter(this.categoriesFilterModel.selection);
        await this.storeAndSetTagsFilter(this.tagsFilterModel.selection);
        await this.storeAndSetDeliveryMethodsFilter(this.deliveryMethodsFilterModel.selection);
        await this.storeAndSetGroupnameFilter(this.groupnameFilterModel.text);
        await this.storeAndSetGroup1Filter(this.group1FilterModel.selection);
        await this.storeAndSetGroup2Filter(this.group2FilterModel.selection);
        await this.storeAndSetDeadlineFilter(this.deadlineApril30thModel.selected, this.deadlineJune15thModel.selected);

        if (Array.isArray(this.assigneesFilterGeneratorService.models())) {
            for (const model of this.assigneesFilterGeneratorService.models()) {
                await this.storeAndSetAssigneeFilter(model);
            }
        }
    }

    getFavoritesFilters(): FavoriteFilters[] {
        const filtersJson = localStorage.getItem(this.favoriteFiltersLocalStorageKey);
        return filtersJson ? (JSON.parse(filtersJson) as FavoriteFilters[]) : [];
    }

    focusFilterNameInput() {
        setTimeout(() => {
            if (this.filterNameInput) {
                this.filterNameInput.nativeElement.focus();
            }
        });
    }

    openModal(modalType: "save" | "rename" | "delete" | "pending") {
        this.modalActionType = modalType === "rename" ? "rename" : "save";
        this.focusFilterNameInput();
        if (modalType === "save" || modalType === "rename") {
            this.bsModalRef = this.modalService.show(this.saveFilterDialogue, {
                class: "custom-modal-rename-dashboard",
            });
        } else if (modalType === "delete") {
            this.bsModalRef = this.modalService.show(this.deleteFilterDialogue, {
                class: "custom-modal-rename-dashboard",
            });
        }
    }

    private async loadGroupEnabledState(): Promise<void> {
        const groups = await this.ifirmGroupsService.getiFirmGroups();
        this.isGroup1Enabled = groups.group1.enabled && groups.group1.group1ContactsEnabled;
        this.isGroup2Enabled = groups.group2.enabled && groups.group2.group2ContactsEnabled;
    }

    private showPendingChangesPopup(onDiscardCallback?: () => void) {
        this.bsModalRef = this.modalService.show(PendingChangesPopupComponent, {
            initialState: {
                onDiscard: () => {
                    this.helpersService.favouriteFiltersPendingChangesEmitter.next(false);
                    this.isFavoriteFilterEditMode = false;
                    if (this.currentFavoriteFilter?.id) {
                        this.applyFavoriteFilter(this.currentFavoriteFilter);
                    }
                    if (onDiscardCallback) {
                        onDiscardCallback();
                    }
                },
                onKeepEditing: () => {
                    // Just close modal, keep filter panel open
                },
            },
            class: "custom-modal-rename-dashboard",
        });
        // Pass the modal ref to the component after it's created
        if (this.bsModalRef.content) {
            this.bsModalRef.content.bsModalRef = this.bsModalRef;
        }
    }

    onAnyFilterChanged() {
        if (this.isFavoriteFilterEditMode) {
            this.helpersService.favouriteFiltersPendingChangesEmitter.next(true);
        }
    }

    async onDeliveryMethodsChanged(selectedOptions: Option[]) {
        await this.storeAndSetDeliveryMethodsFilter(selectedOptions);
        this.filtersService.handleFilterChange(true);
    }

    async storeAndSetDeliveryMethodsFilter(selectedOptions: Option[]) {
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        LocalStorageService.setObject(`Filter_Selection_${currentDashboard.Id}`, ["DeliveryMethods"], selectedOptions);
        this.setDeliveryMethodsFilter(selectedOptions);
    }

    setDeliveryMethodsFilter(selectedOptions: Option[]) {
        const selectedValues = selectedOptions.map((option) => option.value as string);
        const filter = this.deliveryMethodsFilterGeneratorService.generateFilter(selectedValues);
        if (!filter) {
            this.filtersService.activeFilters.delete("DeliveryMethods");
            this.filtersService.activeFiltersForPrint.delete("DeliveryMethods");
        } else {
            this.filtersService.activeFilters.set("DeliveryMethods", filter);
            this.filtersService.activeFiltersForPrint.set(
                "DeliveryMethods",
                this.deliveryMethodsFilterGeneratorService.getPrintDescription(this.deliveryMethodsFilterModel.label, selectedOptions),
            );
        }
    }

    private trackSaveFilterAnalytics(action: "Create" | "Update", selections: Record<string, any>): void {
        const isActiveArr = (arr: any[]) => Array.isArray(arr) && arr.length > 0;
        const isActiveText = (val: string) => typeof val === "string" && val.length > 0;
        const isActiveBool = (val: boolean) => val === true;
        const getOptionDisplay = (option: any): string => {
            if (!option) return "";
            if (typeof option.text === "string" && option.text) return this.translateService.instant(option.text);
            if (option.text && typeof option.text === "object") {
                return option.text["en-CA"] ?? String(option.value ?? "");
            }
            return String(option.value ?? "");
        };
        const assigneeModels = this.assigneesFilterGeneratorService.models();
        const assigneeSelections = Array.isArray(selections.assignees) ? selections.assignees : [];
        const roleSelectionCounts = assigneeSelections.reduce(
            (counts, roleSelection: any[], index: number) => {
                if (!Array.isArray(roleSelection) || roleSelection.length === 0) return counts;

                const modelId = assigneeModels[index]?.id;
                if (!modelId?.startsWith("role-")) return counts;

                const roleId = modelId.replace("role-", "");
                const role = this.iFirmRolesService.iFirmRoles.find((item) => item.Id === roleId);
                if (!role) return counts;

                switch (roleId) {
                    case MANAGER_ROLE_ID:
                        counts.contactManagerCount = roleSelection.length;
                        break;
                    case PARTNER_ROLE_ID:
                        counts.contactPartnerCount = roleSelection.length;
                        break;
                    case PREPARER_ROLE_ID:
                        counts.preparerCount = roleSelection.length;
                        break;
                    case REVIEWER_ROLE_ID:
                        counts.reviewerCount = roleSelection.length;
                        break;
                }

                if (!role.Default) {
                    counts.customRolesCount += roleSelection.length;
                }

                return counts;
            },
            {
                contactManagerCount: 0,
                contactPartnerCount: 0,
                preparerCount: 0,
                reviewerCount: 0,
                customRolesCount: 0,
            },
        );

        const hasAlerts = isActiveArr(selections.alerts);
        const hasCategories = isActiveArr(selections.categories);
        const hasTags = isActiveArr(selections.tags);
        const hasDeliveryMethods = isActiveArr(selections.deliveryMethods);
        const hasGroupName = isActiveText(selections.groupname);
        const hasGroup1 = isActiveArr(selections.group1);
        const hasGroup2 = isActiveArr(selections.group2);
        const hasDeadlineApril30th = isActiveBool(selections.deadlineApril30th);
        const hasDeadlineJune15th = isActiveBool(selections.deadlineJune15th);
        const hasContactManager = roleSelectionCounts.contactManagerCount > 0;
        const hasContactPartner = roleSelectionCounts.contactPartnerCount > 0;
        const hasPreparer = roleSelectionCounts.preparerCount > 0;
        const hasReviewer = roleSelectionCounts.reviewerCount > 0;
        const hasCustomRoles = roleSelectionCounts.customRolesCount > 0;
        const hasReadyToBeWorkedOn = isActiveBool(selections.readyToBeWorkedOn);

        const filtersUsed: string[] = [];

        if (hasAlerts) filtersUsed.push("Alerts");
        if (hasCategories) filtersUsed.push("Categories");
        if (hasTags) filtersUsed.push("Tags");
        if (hasDeliveryMethods) filtersUsed.push("Delivery methods");
        if (hasGroupName) filtersUsed.push("Group name");
        if (hasGroup1) filtersUsed.push("Contact group 1");
        if (hasGroup2) filtersUsed.push("Contact group 2");
        if (hasDeadlineApril30th) filtersUsed.push("Deadline - April 30th");
        if (hasDeadlineJune15th) filtersUsed.push("Deadline - June 15th");
        if (hasContactPartner) filtersUsed.push("Contact partner");
        if (hasContactManager) filtersUsed.push("Contact manager");
        if (hasPreparer) filtersUsed.push("Preparer");
        if (hasReviewer) filtersUsed.push("Reviewer");
        if (hasCustomRoles) filtersUsed.push("Custom roles");
        if (hasReadyToBeWorkedOn) filtersUsed.push("Ready to be worked on");

        const analyticsPayload: Record<string, any> = {
            action,
            filters_used: filtersUsed,
        };

        if (hasAlerts) {
            analyticsPayload.alerts_cards = Array.isArray(selections.alerts)
                ? selections.alerts.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasCategories) {
            analyticsPayload.category = Array.isArray(selections.categories)
                ? selections.categories.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasTags) {
            analyticsPayload.tags = Array.isArray(selections.tags) ? selections.tags.map((o: any) => getOptionDisplay(o)).filter(Boolean) : [];
        }
        if (hasDeliveryMethods) {
            analyticsPayload.delivery_methods = Array.isArray(selections.deliveryMethods)
                ? selections.deliveryMethods.map((o: any) => getOptionDisplay(o)).filter(Boolean)
                : [];
        }
        if (hasGroupName) analyticsPayload.group_name = selections.groupname ?? "";
        if (hasGroup1) analyticsPayload.contact_group_1_count = Array.isArray(selections.group1) ? selections.group1.length : 0;
        if (hasGroup2) analyticsPayload.contact_group_2_count = Array.isArray(selections.group2) ? selections.group2.length : 0;
        if (hasContactPartner) analyticsPayload.contact_partner_count = roleSelectionCounts.contactPartnerCount;
        if (hasContactManager) analyticsPayload.contact_manager_count = roleSelectionCounts.contactManagerCount;
        if (hasPreparer) analyticsPayload.preparer_count = roleSelectionCounts.preparerCount;
        if (hasReviewer) analyticsPayload.reviewer_count = roleSelectionCounts.reviewerCount;

        if (hasDeadlineApril30th || hasDeadlineJune15th) {
            const deadlineValues: string[] = [];
            if (hasDeadlineApril30th) deadlineValues.push("April 30th");
            if (hasDeadlineJune15th) deadlineValues.push("June 15th");
            analyticsPayload.deadline = deadlineValues;
        }
        if (hasCustomRoles) analyticsPayload.custom_roles = String(roleSelectionCounts.customRolesCount);
        if (hasReadyToBeWorkedOn) analyticsPayload.cardReadyToBeWorkedOn = "Yes";

        this.analyticsService.track("Save a filter", analyticsPayload);
    }
}
